import { FC, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Step, StepLabel, Stepper } from '@mui/material';
import { SignUser } from '../../../common/SignUser';
import { useTranslation } from 'react-i18next';
import { doNothing } from '../../../../utils/shared';

interface User {
  id: string;
  name: string;
  skippable?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  users: User[];
  onSign: (signedDocuments: { userId: string; file: File }[]) => void;
}

export const SignUsersModal: FC<Props> = ({ open, onClose, users, onSign }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isEmpty, setIsEmpty] = useState(false);
  const [signedDocuments, setSignedDocuments] = useState<{ userId: string; file: File }[]>([]);
  const currentUser = users[activeStep];
  const { t } = useTranslation();

  const handleNext = () => {
    if (activeStep === users.length - 1) {
      // Move onSign to useEffect
      setActiveStep(0);
      // onSign(signedDocuments);
      onClose();
    } else if (activeStep < users.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  // Call onSign when signedDocuments is filled and last step is reached
  useEffect(() => {
    if (activeStep === 0 && signedDocuments.length === users.length) {
      onSign(signedDocuments);
      setSignedDocuments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, signedDocuments, users.length]);

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setSignedDocuments([]);
    }
  }, [open]);

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
    onClose();
  };

  const handleSign = (file: File) => {
    setSignedDocuments((prev) => [...prev, { userId: currentUser.id, file }]);
    handleNext();
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('invitation.sign')}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel>
          {users.map((user) => (
            <Step key={user.id}>
              <StepLabel>{user.name}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box mt={2}>
          <SignUser
            handleIsEmpty={setIsEmpty}
            playground
            hideSelect
            userList={[currentUser]}
            save={async () => doNothing()}
            nextButtonLabel={activeStep === users.length - 1 ? t('invitation.saveSignatures') : t('invitation.nextSignature')}
            nextButtonSkippableLabel={isEmpty ? t('invitation.withoutSignature') : undefined}
            saveFile={async (userId, file) => {
              if (file) {
                handleSign(file);
              }
            }}
            allowSkip={currentUser?.skippable}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleBack}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignUsersModal;
